/**
 * Ingest tokens, PLAN.md §7. The ingest service does not speak Convex Auth, so
 * a Convex action mints a short-lived HMAC over `recordingId|exp` and ingest
 * verifies it locally — no round trip on connect.
 *
 * Web Crypto only, so the identical code runs in a Convex action and in Node 22.
 */

export const INGEST_TOKEN_TTL_MS = 12 * 60 * 60 * 1000;

const encoder = new TextEncoder();

function base64url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function sign(secret: string, message: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(message));
  return base64url(new Uint8Array(signature));
}

/** `<recordingId>.<expMs>.<hmac>` — opaque to the client, which only relays it. */
export async function mintIngestToken(
  secret: string,
  recordingId: string,
  expiresAt: number,
): Promise<string> {
  const body = `${recordingId}.${expiresAt}`;
  return `${body}.${await sign(secret, body)}`;
}

export type TokenVerdict =
  | { ok: true; recordingId: string; expiresAt: number }
  | { ok: false; reason: "malformed" | "bad_signature" | "expired" };

export async function verifyIngestToken(
  secret: string,
  token: string,
  nowMs: number = Date.now(),
): Promise<TokenVerdict> {
  const parts = token.split(".");
  if (parts.length !== 3) return { ok: false, reason: "malformed" };
  const [recordingId, expText, signature] = parts;
  const expiresAt = Number(expText);
  if (recordingId.length === 0 || !Number.isFinite(expiresAt)) {
    return { ok: false, reason: "malformed" };
  }
  const expected = await sign(secret, `${recordingId}.${expText}`);
  if (!timingSafeEqual(expected, signature)) {
    return { ok: false, reason: "bad_signature" };
  }
  // Signature first, then expiry: an attacker learns nothing from the ordering.
  if (nowMs > expiresAt) return { ok: false, reason: "expired" };
  return { ok: true, recordingId, expiresAt };
}

/** Constant-time over the compared bytes; length is not a secret here. */
export function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
